/* eslint-disable no-underscore-dangle, no-useless-escape */
import L from 'leaflet';
import 'leaflet-draw';
import { Meteor } from 'meteor/meteor';
import chroma from 'chroma-js';
import {
  assign,
  find,
  findIndex,
  forEach,
  get,
  includes,
  isBoolean,
  isEmpty,
  isNil,
  isNumber,
  hasIn,
  map
} from 'lodash';
import { GEOJSON_GEOMETRIES, POINT_TYPES, DEFAULT } from '../api/Styles/Styles';
import * as styleOps from '../api/Styles/style-operations';
import * as layerOps from '../api/Layers/layer-operations';
import * as userOps from '../api/Users/user-operations';

import './leaflet-operations/customPopup';
import getConditionFromOperator from './get-condition-from-operator';

const BADGE = '<span class="badge badge-pill badge-info"><i class="fas fa-paint-brush"></i></span>';
const COLOR_WHITE = '#fff';
const CUSTOM = 'custom';
const EMPTY_VALUE = 'read_as_empty_value';
const EXCLUDED = ['bbox', 'uuid'];
const LEAFLET_DEFAULT_MARGIN = 6;
const POINT_CLASS = 'text-center align-middle';
const SVG_START = '<svg';
const SVG_CLOSE = '</svg>';
let oldevent = {};
const EXPRESIONS = {
  WIDTH: /width=('|")[0-9]+(\.[0-9]{0,})?[a-z]{0,2}('|")/,
  HEIGHT: /height=('|")[0-9]+(\.[0-9]{0,})?[a-z]{0,2}('|")/,
  FILL: /fill="#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})"/g,
  CLASS: /class=".+"[\s\n]/g,
  PATH: /<(path|circle|rect|ellipse|line|polyline|polygon)[\s\n]/g,
  SVG: /<svg[\s\n]/g,
  COLOR: /(?:#|0x)(?:[a-f0-9]{3}|[a-f0-9]{6})\b|(?:rgb|hsl)a?\([^\)]*\)/gi,
  ICONWIDTH: /width:[0-9]+(\.[0-9]{0,})?[a-z]{0,2}/,
  ICONHEIGHT: /height:[0-9]+(\.[0-9]{0,})?[a-z]{0,2}/,
  MARGINTOP: /margin-top: -?[0-9]+(\.[0-9]{0,})?[a-z]{0,2}/,
  MARGINLEFT: /margin-left: -?[0-9]+(\.[0-9]{0,})?[a-z]{0,2}/
};
const REGEXS = {
  WIDTH: new RegExp(EXPRESIONS.WIDTH),
  HEIGHT: new RegExp(EXPRESIONS.HEIGHT),
  FILL: new RegExp(EXPRESIONS.FILL),
  CLASS: new RegExp(EXPRESIONS.CLASS),
  COLOR: new RegExp(EXPRESIONS.COLOR)
};

/**
 * Changes the size of the point feature based on the scale change.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.newLayerToDraw - The leaflet layer containing the features to change.
 * @param {number} arg.scale - Thevalue of the scale change.
 * @param {boolean} [arg.isMarker] - Flag that indicates if the feature is a marker or not.
 * @param {boolean} [arg.isCustom] - Flag that indicates if the style is custom or not.
 */
export const scaleIconSize = ({ newLayerToDraw, scale, isMarker = false, isCustom = false }) => {
  const expresionWidth = isMarker ? EXPRESIONS.WIDTH : EXPRESIONS.ICONWIDTH;
  const expresionHeight = isMarker ? EXPRESIONS.HEIGHT : EXPRESIONS.ICONHEIGHT;
  // eslint-disable-next-line no-underscore-dangle
  forEach(newLayerToDraw._layers, marker => {
    const newIcon = get(marker, ['options', 'icon'], {});
    if (isCustom) {
      if (hasIn(newIcon, ['options', 'html'])) {
        newIcon.options.html = newIcon.options.html.replace(EXPRESIONS.WIDTH, match => {
          const prevWidth = Number(match.split('=')[1].slice(1, -1));
          return `width='${prevWidth * scale}'`;
        });
        newIcon.options.html = newIcon.options.html.replace(EXPRESIONS.HEIGHT, match => {
          const prevHeight = Number(match.split('=')[1].slice(1, -1));
          return `height='${prevHeight * scale}'`;
        });
        newIcon.options.html = newIcon.options.html.replace(EXPRESIONS.MARGINTOP, match => {
          const prevMargin = Number(match.split(':')[1].slice(1, -2));
          return `margin-top: ${prevMargin * scale}px`;
        });
        newIcon.options.html = newIcon.options.html.replace(EXPRESIONS.MARGINLEFT, match => {
          const prevMargin = Number(match.split(':')[1].slice(1, -2));
          return `margin-left: ${prevMargin * scale}px`;
        });
        marker.setIcon(newIcon);
      } else if (hasIn(newIcon, ['options', 'iconSize'])) {
        newIcon.options.iconSize = [
          newIcon.options.iconSize[0] * scale,
          newIcon.options.iconSize[1] * scale
        ];
        marker.setIcon(newIcon);
      }
    } else {
      let prevSize;
      newIcon.options.html = newIcon.options.html.replace(expresionWidth, match => {
        prevSize = isMarker
          ? Number(match.split('=')[1].slice(1, -3))
          : Number(match.split(':')[1]);
        if (isMarker) return `width="${prevSize * scale}px"`;
        return `width:${prevSize * scale}`;
      });
      newIcon.options.html = newIcon.options.html.replace(
        expresionHeight,
        isMarker ? `height="${prevSize * scale}px"` : `height:${prevSize * scale}`
      );
      newIcon.options.html = newIcon.options.html.replace(
        EXPRESIONS.MARGINTOP,
        `margin-top: ${-((prevSize * scale) / 2 - LEAFLET_DEFAULT_MARGIN)}px`
      );
      newIcon.options.html = newIcon.options.html.replace(
        EXPRESIONS.MARGINLEFT,
        `margin-left: ${-((prevSize * scale) / 2 - LEAFLET_DEFAULT_MARGIN)}px`
      );
      marker.setIcon(newIcon);
    }
  });
};

/**
 * Sets the style to highlight a feature when popup is active.
 *
 * @param {object} event - The event that activates the function.
 */
const highlightFeature = event => {
  const layer = event.target;
  const user = Meteor.user();
  if (layer.setStyle) {
    layer.setStyle({
      color: userOps.getFeatureSelectedColor(user),
      dashArray: '',
      weight: userOps.getFeatureSelectedWeight(user)
    });
  }
};

/**
 * Resets the style to default of a feature when popup is no longer active.
 *
 * @param {object} event - The event that activates the function.
 * @param {object} mainStyle - The created style object.
 */
const resetHighlight = (event, mainStyle) => {
  const layer = event.target;
  if (layer.setStyle) {
    layer.setStyle(mainStyle);
  }
};

/**
 * Sets the style to highlight a Point Geometry [Icon,Marker] feature when popup is active.
 *
 * @param {object} event - The event that activates the function.
 */
const highlightMarkerFeature = event => {
  const layer = event.target;
  const user = Meteor.user();
  const auxFuction = layerPoint => {
    if (!isEmpty(layerPoint._icon.getElementsByClassName('fa-lg'))) {
      const svg = layerPoint._icon.getElementsByClassName('fa-lg')[0];
      assign(svg.style, {
        stroke: userOps.getFeatureSelectedColor(user),
        strokeWidth: `${userOps.getFeatureSelectedWeight(user) * 5}`,
        strokeDasharray: ''
      });
    } else if (!isEmpty(layerPoint._icon.getElementsByClassName('path-styled'))) {
      const styledElements = layerPoint._icon.getElementsByClassName('path-styled');
      forEach(styledElements, element => {
        element.setAttribute('stroke', userOps.getFeatureSelectedColor(user));
        element.setAttribute('stroke-width', `${userOps.getFeatureSelectedWeight(user) * 3}`);
        element.setAttribute('stroke-dasharray', '');
      });
    } else {
      // eslint-disable-next-line no-param-reassign
      layerPoint._icon.style.filter = 'invert(1)';
    }
  };
  if (hasIn(layer, '_icon')) {
    auxFuction(layer);
  } else if (hasIn(layer, '_layers')) {
    map(layer._layers, layerPoint => {
      auxFuction(layerPoint);
    });
  }
};

/**
 * Resets the style to default of a Point Geometry [Icon,Marker] feature when popup is no longer active.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.event - The event that activates the function.
 * @param {object} arg.mainStyle - The created style object.
 */
const resetMarkerHighlight = ({ event, mainStyle }) => {
  const layer = event.target;
  const auxFuction = layerPoint => {
    if (!isEmpty(layerPoint._icon.getElementsByClassName('fa-lg'))) {
      const svg = layerPoint._icon.getElementsByClassName('fa-lg')[0];
      assign(svg.style, {
        stroke: mainStyle.color,
        strokeWidth: mainStyle.weight,
        strokeDasharray: mainStyle.dashArray
      });
    } else if (!isEmpty(layerPoint._icon.getElementsByClassName('path-styled'))) {
      const styledElements = layerPoint._icon.getElementsByClassName('path-styled');
      forEach(styledElements, element => {
        element.setAttribute('stroke', mainStyle.color);
        element.setAttribute('stroke-width', mainStyle.weight);
        element.setAttribute('stroke-dasharray', mainStyle.dashArray);
      });
    } else {
      // eslint-disable-next-line no-param-reassign
      layerPoint._icon.style.filter = 'invert(0)';
    }
  };
  if (hasIn(layer, '_icon')) {
    auxFuction(layer);
  } else if (hasIn(layer, '_layers')) {
    map(layer._layers, layerPoint => {
      auxFuction(layerPoint);
    });
  }
};

/**
 * Get the html for the popup of the feature.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.properties - The properties of the feature.
 * @param {string} arg.styledProp - The property to look for in the geojson feature.
 * @returns {string} The html for the popup for that feature.
 */
export const getPopup = ({ properties, styledProp, name }) => {
  let popup = `<p style="word-wrap: break-word; margin: 0;min-width: 10vw;"><h4><b>${name}</b></h4></p><hr>`;
  forEach(properties, (value, property) => {
    const isValuePresentable = isNumber(value) || isBoolean(value) || !isEmpty(value);
    const isInPopup = isValuePresentable && !includes(EXCLUDED, property);

    if (isInPopup) {
      if (property === styledProp) {
        popup += `
          <div class="line-container" >
            <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
              <b style="white-space: nowrap;">${BADGE} ${property}: </b>${value}
            </div>
          </div>
        `;
      } else {
        popup += `
          <div class="line-container">
            <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
              <b>${property}: </b>${value}
            </div>
          </div>
        `;
      }
    }
  });
  return popup;
};

/**
 * Get the html for the popup of the feature.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.properties - The properties of the feature.
 * @param {string} arg.styledProp - The property to look for in the geojson feature.
 * @returns {string} The html for the popup for that feature.
 */
export const getPopupAnnotations = ({ properties, name }) => {
  let popup = `<p style="word-wrap: break-word; margin: 0;width: 30vw;"><h4><b>${name} - ${properties.template.name}</b></h4></p><hr>`;

  properties.template.elements.map(item => {
    if (item.elementType === 'icon') {
      if (!isEmpty(item.caption)) {
        popup += `
        <div class="line-container">
          <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
            <p style="word-wrap: break-word; margin: 0;min-width: 10vw;"><h6><b>${item.caption}</b></h6></p>
            <img src=${item.value} alt="Descripción de la imagen" style="max-width: 30vw; height: auto">
          </div>
        </div>
      `;
      } else {
        popup += `
        <div class="line-container">
          <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
            <img src=${item.value} alt="Descripción de la imagen" style="max-width: 30vw; height: auto">
          </div>
        </div>
      `;
      }
    } else if (item.elementType === 'iframe') {
      popup += `
                <div class="line-container">
                  <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
                    <iframe src=${item.value} height="400" width="800" name="demo"></iframe>
                  </div>
                </div>
              `;
    } else if (item.elementType === 'image') {
      if (!isEmpty(item.value)) {
        if (!isEmpty(item.caption)) {
          popup += `
            <div class="line-container">
              <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
                <p style="word-wrap: break-word; margin: 0;min-width: 10vw;"><h6><b>${item.caption}</b></h6></p>
                <img src=${item.value} alt="Descripción de la imagen" style="max-width: 30vw; height: auto">
              </div>
            </div>
          `;
        } else {
          popup += `
            <div class="line-container">
              <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
                <img src=${item.value} alt="Descripción de la imagen" style="max-width: 30vw; height: auto">
              </div>
            </div>
          `;
        }
      }
    } else if (
      item.elementType === 'textarea' ||
      (item.elementType === 'input' && item.dataType === 'text')
    ) {
      if (!isEmpty(item.value)) {
        popup += `
        <div class="line-container">
          <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
            <p style="max-width: 30vw; word-wrap: break-word;"><h6><b>${item.value}</b></h6></p><hr>
          </div>
        </div>
      `;
      }
    } else if (item.elementType === 'video') {
      const obtenerVideoIdDesdeUrl = url => {
        const match = url.match(
          /(?:\?v=|&v=|youtu\.be\/|\/embed\/|\/v\/|\/e\/|youtu\.be\/|\/c\/|\/u\/|\/user\/\w+\/|embed\/|user\/\w+\/videos\/|\/v\/|\/R\/|\/C\/|\/E\/|\/G\/|\/H\/|\/J\/|\/K\/|\/L\/|\/N\/|\/P\/|\/Q\/|\/S\/|\/U\/|\/V\/|\/W\/|\/Y\/|\/hq\/|\/hqdefault\/|\/maxresdefault\/|\/thumbnail\/|\/1.jpg)([^#\&\?]*).*/
        );
        return match && match[1].length === 11 ? match[1] : null;
      };

      const obtenerSitioDesdeUrl = url => {
        const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+)\./i);
        return match ? match[1].toLowerCase() : null;
      };

      const agregarEmbed = url => {
        if (url.includes('/embed/')) {
          return url;
        }
        const sitio = obtenerSitioDesdeUrl(url);

        if (sitio === 'youtube' || sitio === 'youtu') {
          const videoId = obtenerVideoIdDesdeUrl(url);
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
        return url;
      };

      if (!isEmpty(item.caption) && !isEmpty(item.value)) {
        const videoURL = agregarEmbed(item.value);
        popup += `
        <div class="line-container">
          <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
            <p style="word-wrap: break-word; margin: 0;min-width: 10vw;"><h6><b>${item.caption}</b></h6></p>
            <iframe width="100%" height="315" src=${videoURL} frameborder="0" style="max-width: 30vw"></iframe>
          </div>
        </div>
      `;
      } else if (!isEmpty(item.value)) {
        const videoURL = agregarEmbed(item.value);
        popup += `
        <div class="line-container">
          <div class="single-line" style="max-width: 30vw; word-wrap: break-word; margin-bottom: 0.5vh;">
            <iframe width="100%" height="315" src=${videoURL} frameborder="0" style="max-width: 30vw"></iframe>
          </div>
        </div>
      `;
      }
    }
    return popup;
  });

  return popup;
};

export const whenClicked = e => {
  localStorage.setItem('Propperties', JSON.stringify(e));
};
/**
 * Get the value of styled property in the feature given.
 *
 * @param {object} arg - The object argument.
 * @param {object} arg.feature - The geojson feature.
 * @param {object} arg.layer - The layer.
 * @param {string} arg.styledProp - The property to look for in the geojson feature.
 * @returns {*} The value of the property for that feature.
 */
const getFeatureValue = ({ feature, layer, styledProp }) => {
  if (layerOps.isAnnotationsLayer(layer) || layerOps.isAnnotationsRealTime(layer)) {
    if (hasIn(feature, ['properties', styledProp])) return feature.properties.template.icon;
    if (hasIn(feature, styledProp)) return feature.properties.template.icon;
  } else {
    if (hasIn(feature, ['properties', styledProp])) return feature.properties[styledProp];
    if (hasIn(feature, styledProp)) return feature[styledProp];
  }

  return '';
};

/**
 * Build the leaflet style for the corresponding geometry type.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {Function} arg.getSimbologyColor - A function that calculates the color for the feature.
 * @param {object} arg.layer - The style object.
 * @param {string} arg.pane - The pane of the layer (need in maps).
 * @param {boolean} arg.popupProp - The prop of the popup, if true it shows the popup.
 * @param {object} arg.style - The style object.


 * @returns {object} The leaflet style to apply to the layer.
 */
const getGeometryStyle = async ({ style, layer, getSimbologyColor, pane, popupProp }) => {
  const styleOptions = {};
  const iconClass = styleOps.getIconClass(style);
  const styledProp = styleOps.getStyledProp(style);
  const pointType = styleOps.getPointType(style);
  let pointMarkerHTML = {};
  let geometry = styleOps.getGeoJSONGeometry(style);
  if (
    (layerOps.isGeoJSONVT(layer) || layerOps.isMapBox(layer)) &&
    styleOps.isGeometryPoint(style)
  ) {
    geometry = GEOJSON_GEOMETRIES.POLYGON;
  }

  switch (true) {
    case GEOJSON_GEOMETRIES.POINT === geometry:
      if (pointType === POINT_TYPES.ICON) {
        pointMarkerHTML = feature => `
          <i
            class="fas fa-${iconClass} fa-lg"
            style="
              opacity:${styleOps.getAlpha(style)};
              color:${getSimbologyColor(feature)};
              width:${styleOps.getPointSize(style)};
              height:${styleOps.getPointSize(style)};
              margin-top: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
              margin-left: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
              stroke:${styleOps.getBorderColor(style)};
              stroke-width:${styleOps.getWeight(style)};
              stroke-dashArray:${styleOps.getBorderStyle(style)};
            "
          />
        `;
        styleOptions.pointToLayer = (feature, latlng) => {
          const options = {
            icon: L.divIcon({ html: pointMarkerHTML(feature), className: POINT_CLASS })
          };
          if (!isEmpty(pane)) options.pane = pane;
          return L.marker(latlng, options);
        };
      }
      if (pointType === POINT_TYPES.CIRCLEMARKER) {
        styleOptions.pointToLayer = (feature, latlng) => {
          const options = {
            radius: styleOps.getPointSize(style),
            color: styleOps.getBorderColor(style),
            opacity: styleOps.getBorderAlpha(style),
            fillColor: getSimbologyColor(feature),
            fillOpacity: styleOps.getAlpha(style),
            weight: styleOps.getWeight(style),
            dashArray: styleOps.getBorderStyle(style)
          };
          if (!isEmpty(pane)) options.pane = pane;
          if (styleOps.isScaledSymbology(style)) {
            return L.circle(latlng, options);
          }
          return L.circleMarker(latlng, options);
        };
      }
      if (pointType === POINT_TYPES.MARKER) {
        try {
          const response = await fetch(styleOps.getSVGUrl(style));
          if (response.ok) {
            const text = await response.text();
            const initialPosition = text.search(SVG_START);
            const endingPosition = text.search(SVG_CLOSE) + SVG_CLOSE.length;
            let svg = text.slice(initialPosition, endingPosition);
            const svgStyle = `style="
              margin-top: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
              margin-left: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
              overflow: visible;
            "\n`;
            if (svg.match(REGEXS.WIDTH)) {
              svg = svg.replace(EXPRESIONS.WIDTH, `width="${styleOps.getPointSize(style)}px"\n`);
            } else {
              svg = svg.replace(
                `${SVG_START} `,
                `${SVG_START} width="${styleOps.getPointSize(style)}px"\n`
              );
            }
            if (svg.match(REGEXS.HEIGHT)) {
              svg = svg.replace(EXPRESIONS.HEIGHT, `height="${styleOps.getPointSize(style)}px"\n`);
            } else {
              svg = svg.replace(
                `${SVG_START} `,
                `${SVG_START} height="${styleOps.getPointSize(style)}px"\n`
              );
            }
            svg = svg.replace(EXPRESIONS.SVG, match => `${match}${svgStyle}`);
            svg = svg.replace(
              EXPRESIONS.PATH,
              match =>
                `${match}stroke="${styleOps.getBorderColor(style)}"
                stroke-width="${styleOps.getWeight(style)}"
                stroke-dashArray="${styleOps.getBorderStyle(style)}" `
            );
            pointMarkerHTML = feature => {
              if (getSimbologyColor(feature) !== DEFAULT) {
                svg = svg.replace(
                  EXPRESIONS.PATH,
                  match => `${match}fill="${getSimbologyColor(feature)}"\n`
                );
                if (svg.match(REGEXS.COLOR)) {
                  svg = svg.replace(EXPRESIONS.COLOR, match => {
                    if (match === styleOps.getBorderColor(style)) {
                      return styleOps.getBorderColor(style);
                    }
                    return getSimbologyColor(feature);
                  });
                }
              }
              if (svg.match(REGEXS.CLASS)) {
                svg = svg.replace(EXPRESIONS.CLASS, 'class="path-styled"\n');
              } else {
                svg = svg.replace(EXPRESIONS.PATH, match => `${match}class="path-styled"\n`);
              }
              return svg;
            };
          }
        } catch (error) {
          pointMarkerHTML = feature => {
            const hasColor = !isEmpty(getSimbologyColor(feature).match(REGEXS.COLOR));
            const color = hasColor ? getSimbologyColor(feature) : DEFAULT;
            return `
                <div
                  style="
                    width:${styleOps.getPointSize(style)}px;
                    height:${styleOps.getPointSize(style)}px;
                    opacity:${styleOps.getAlpha(style)};
                    background: ${color};
                    margin-top: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
                    margin-left: -${styleOps.getPointSize(style) / 2 - LEAFLET_DEFAULT_MARGIN}px;
                    -webkit-mask-image:url(${styleOps.getSVGUrl(style)});
                    -webkit-mask-size: 100% 100%;
                    -webkit-mask-position: 50% 50%;
                    -webkit-mask-repeat: no-repeat
                  "
                />
              `;
          };
        }
        styleOptions.pointToLayer = (feature, latlng) => {
          const options = {
            icon: L.divIcon({
              html: pointMarkerHTML(feature),
              className: POINT_CLASS
            })
          };
          if (!isEmpty(pane)) options.pane = pane;
          return L.marker(latlng, options);
        };
      }
      break;
    case GEOJSON_GEOMETRIES.LINESTRING === geometry:
      styleOptions.style = feature => ({
        color: getSimbologyColor(feature),
        opacity: styleOps.getAlpha(style),
        weight: styleOps.getWeight(style),
        dashArray: styleOps.getBorderStyle(style)
      });
      break;
    case GEOJSON_GEOMETRIES.POLYGON === geometry:
    case GEOJSON_GEOMETRIES.MULTIPOLYGON === geometry:
      styleOptions.style = feature => ({
        radius: styleOps.getPointSize(style),
        color: styleOps.getBorderColor(style),
        opacity: styleOps.getBorderAlpha(style),
        fillColor: getSimbologyColor(feature),
        fillOpacity: styleOps.getAlpha(style),
        weight: styleOps.getWeight(style),
        dashArray: styleOps.getBorderStyle(style)
      });
      break;
    case styleOps.isCustom(style):
      styleOptions.style = getSimbologyColor;
      styleOptions.pointToLayer = getSimbologyColor;
      break;
    default:
      console.error('Wrong geometry data');
      break;
  }
  styleOptions.onEachFeature = (feature, geojsonLayer) => {
    if (feature.properties) {
      // TODO: Choose one here or let users decide each layer
      /* ON HOVER */
      // const popup = layer.bindPopup(getPopup(feature.properties));
      // layer.on('mouseover', () => popup.openPopup());
      // layer.on('mouseout', () => popup.closePopup());
      /* ON CLICK */
      geojsonLayer.on({
        click: () =>
          whenClicked({
            id: layerOps.getId(layer),
            name: layerOps.getName(layer),
            properties: feature.properties
          })
      });
      if (
        (layerOps.isAnnotationsLayer(layer) || layerOps.isAnnotationsRealTime(layer)) &&
        layer.annotationLayerProperties.geometry.key === 'Point'
      ) {
        geojsonLayer.bindPopup(
          L.customPopup().setContent(
            getPopupAnnotations({
              properties: feature.properties,
              styledProp,
              name: layerOps.getName(layer)
            })
          )
        );
      } else if (popupProp !== true) {
        geojsonLayer.bindPopup(
          L.customPopup().setContent(
            getPopup({
              properties: feature.properties,
              styledProp,
              name: layerOps.getName(layer)
            })
          )
        );
      }
    }
    const mainStyle = {
      color: styleOps.isGeometryLine(style)
        ? getSimbologyColor(feature)
        : styleOps.getBorderColor(style),
      dashArray: styleOps.getBorderStyle(style),
      weight: styleOps.getWeight(style)
    };

    let onPopupOpen = highlightFeature;
    let onPopupClose = event => resetHighlight(event, mainStyle);
    const onClickFeature = event => {
      if (!isEmpty(oldevent)) {
        resetHighlight(oldevent, mainStyle);
      }

      highlightFeature(event);
      oldevent = event;
    };
    if (includes([POINT_TYPES.MARKER, POINT_TYPES.ICON], pointType)) {
      onPopupOpen = highlightMarkerFeature;
      onPopupClose = event => resetMarkerHighlight({ event, mainStyle });
    }

    geojsonLayer.on({
      click: onClickFeature,
      popupopen: onPopupOpen,
      popupclose: onPopupClose
    });
  };
  return styleOptions;
};

/**
 * Build the leaflet style for the corresponding simbology type.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @param {string} arg.pane - The pane of the layer (need in maps).
 * @param {object} arg.customStyleObject - The object containing the custom styles (need in custom styles).
 * @param {boolean} arg.popupProp - The prop of the popup, if true it shows the popup.
 * @returns {object} The leaflet style to apply to the layer.
 */
export const getGeoJSONStyle = async ({
  layer,
  style,
  pane = '',
  customStyleObject = {},
  popupProp
}) => {
  let getSimbologyColor = {};

  const styledProp = styleOps.getStyledProp(style);
  const colorRange = styleOps.getColorRange(style);
  const dataLimits = styleOps.getDataLimits(style);
  const legend = styleOps.getLegend(style);

  const colorScale = chroma.scale(colorRange).classes(dataLimits);
  switch (true) {
    case styleOps.isSingleSimbology(style):
      getSimbologyColor = () => styleOps.getColor(style);
      return getGeometryStyle({ style, layer, getSimbologyColor, pane, popupProp });
    case styleOps.isCategorizedSimbology(style):
      getSimbologyColor = feature => {
        let value = getFeatureValue({ feature, layer, styledProp });
        if (!isNumber(value) && isEmpty(value) && !isBoolean(value)) {
          value = EMPTY_VALUE;
        }
        let color = get(find(legend, ['value', value]), 'color');
        if (isNil(color)) color = '#fff';
        return color;
      };
      return getGeometryStyle({ style, layer, getSimbologyColor, pane, popupProp });
    case styleOps.isNumericSimbology(style):
      getSimbologyColor = feature => {
        const value = getFeatureValue({ feature, layer, styledProp });
        if (colorRange === CUSTOM) {
          const dataLimitIndex = findIndex(
            dataLimits,
            (dataLimit, index) =>
              value >= dataLimit &&
              (value < dataLimits[index + 1] || dataLimit > dataLimits[index + 1])
          );
          if (dataLimitIndex < 0) return legend[legend.length + dataLimitIndex].color;
          return legend[dataLimitIndex].color;
        }
        if ((!isNumber(value) && isEmpty(value)) || value === EMPTY_VALUE) return COLOR_WHITE;
        return colorScale(value);
      };
      return getGeometryStyle({ style, layer, getSimbologyColor, pane, popupProp });
    case styleOps.isCustom(style):
      getSimbologyColor = (feature, latlng) => {
        const { property, isPoint, styles: stylesArray, default: defaultStyle } = customStyleObject;
        const featureValue = getFeatureValue({ feature, layer, styledProp: property });
        let foundStyle;
        const styleObject = find(stylesArray, ({ operator, value }) =>
          getConditionFromOperator({ operator, firstValue: featureValue, secondValue: value })
        );
        if (styleObject) {
          foundStyle = styleObject.style;
        } else {
          foundStyle = defaultStyle;
        }
        if (layerOps.isMapBox(layer) || layerOps.isGeoJSONVT(layer)) return foundStyle;
        if (isPoint && foundStyle) {
          if (get(foundStyle, ['iconUrl'])) {
            const options = {
              icon: L.icon(foundStyle)
            };
            if (!isEmpty(pane)) options.pane = pane;
            return L.marker(latlng, options);
          }
          if (get(foundStyle, ['html'])) {
            const options = {
              icon: L.divIcon(foundStyle)
            };
            if (!isEmpty(pane)) options.pane = pane;
            return L.marker(latlng, options);
          }
          if (!isEmpty(pane)) foundStyle.pane = pane;
          if (styleOps.isScaledSymbology(style)) {
            return L.circle(latlng, foundStyle);
          }
          return L.circleMarker(latlng, foundStyle);
        }
        if (layerOps.getGeoJSONGeometry(layer) === GEOJSON_GEOMETRIES.POINT && foundStyle) {
          if (!isEmpty(pane)) foundStyle.pane = pane;
          if (styleOps.isScaledSymbology(style)) {
            return L.circle(latlng, foundStyle);
          }
          return L.circleMarker(latlng, foundStyle);
        }
        return foundStyle;
      };
      return getGeometryStyle({ style, layer, getSimbologyColor, pane, popupProp });
    default:
      console.error('Wrong style data');
      break;
  }
  return {};
};
